<script setup lang="ts">
import { AdjustmentsHorizontalIcon, ArrowTrendingUpIcon, CheckCircleIcon, CursorArrowRaysIcon, ScaleIcon } from '@heroicons/vue/24/outline'
import { MARKETING_ROUTES, MARKETING_SCREENSHOTS } from '~~/shared/config/marketing'

definePageMeta({ layout: 'marketing' })

useMarketingSeo({
  title: 'Quelle action renforcer et comment suivre son portefeuille | AppTrident',
  description: 'Transformez vos scénarios de valorisation en une décision claire, équilibrez votre portefeuille et suivez votre progression avec AppTrident.',
  path: MARKETING_ROUTES.decide,
})

const workflow = [
  { icon: CursorArrowRaysIcon, title: '1. L\'AppTrident filtre les entreprises éligibles', description: 'L’application part des entreprises qualifiées et de leur rendement annualisé estimé à partir des hypothèses retenues.' },
  { icon: ScaleIcon, title: '2. Elle tient compte de votre allocation', description: 'Parmi les candidates, elle privilégie la position sur laquelle votre portefeuille est le moins exposé.' },
  { icon: ArrowTrendingUpIcon, title: '3. Elle propose une action concrète', description: 'Vous obtenez l’entreprise à renforcer et le montant correspondant à votre plan d’investissement hebdomadaire ou mensuel.' },
]

const purchaseModes = {
  simple: {
    label: 'Mode Simple',
    description: 'Suivez les entreprises et hypothèses Trident pour réduire le temps consacré à la décision.',
    src: MARKETING_SCREENSHOTS.purchase,
    alt: 'Mode Simple de la page Achat AppTrident avec une action recommandée et un montant à investir',
    caption: 'En mode Simple, l\'AppTrident applique la sélection et les hypothèses Trident pour vous proposer l’action à renforcer.',
  },
  advanced: {
    label: 'Mode Avancé',
    description: 'Personnalisez les entreprises, les fondamentaux, la croissance attendue et les multiples.',
    src: MARKETING_SCREENSHOTS.purchaseAdvanced,
    alt: 'Mode Avancé de la page Achat AppTrident avec des hypothèses personnalisées',
    caption: 'En mode Avancé, ajoutez ou retirez des entreprises et adaptez la croissance, l’indicateur et le multiple futur.',
  },
} as const

type PurchaseMode = keyof typeof purchaseModes

const selectedPurchaseMode = ref<PurchaseMode>('simple')
const selectedPurchasePreview = computed(() => purchaseModes[selectedPurchaseMode.value])
const route = useRoute()
const { $posthog } = useNuxtApp()

function selectPurchaseMode(mode: PurchaseMode): void {
  if (mode === selectedPurchaseMode.value) {
    return
  }

  selectedPurchaseMode.value = mode
  $posthog?.capture('marketing_feature_mode_changed', {
    page: 'decider-suivre',
    path: route.path,
    section: 'mode-selector',
    label: purchaseModes[mode].label,
    destination: 'interaction',
  })
}

async function handleModeKeydown(event: KeyboardEvent, currentMode: PurchaseMode): Promise<void> {
  const modeKeys = Object.keys(purchaseModes) as PurchaseMode[]
  const currentIndex = modeKeys.indexOf(currentMode)
  let nextIndex = currentIndex

  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % modeKeys.length
  else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + modeKeys.length) % modeKeys.length
  else if (event.key === 'Home') nextIndex = 0
  else if (event.key === 'End') nextIndex = modeKeys.length - 1
  else return

  event.preventDefault()
  const nextMode = modeKeys[nextIndex]
  if (!nextMode) return
  selectPurchaseMode(nextMode)
  await nextTick()
  document.getElementById(`purchase-mode-${nextMode}`)?.focus()
}
</script>

<template>
  <div>
    <MarketingFeatureHero
      eyebrow="Étape 3 · Décider & Suivre"
      title="Transformez votre analyse en une action claire pour votre portefeuille."
      description="L'AppTrident relie la valorisation de chaque entreprise à votre allocation actuelle pour vous aider à décider quelle position renforcer et combien investir."
      :bullets="['Une recommandation fondée sur des règles visibles.', 'Un mode Simple pour suivre le cadre Trident.', 'Un mode Avancé pour personnaliser votre sélection.']"
      page="decider-suivre"
      preview-type="decision"
      :preview-src="MARKETING_SCREENSHOTS.purchase"
      preview-alt="Page Achat AppTrident avec une action recommandée et une proposition de montant à investir"
      preview-caption="La recommandation combine qualification, rendement estimé et poids actuel dans le portefeuille."
    />
    <MarketingTrustStrip page="decider-suivre" />

    <section class="bg-white py-20 sm:py-28">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeading eyebrow="La page Achat" title="Une logique simple, expliquée à chaque étape" description="L’objectif n’est pas de vous donner un signal mystérieux. Chaque recommandation peut être reliée aux hypothèses de valorisation et à la composition de votre portefeuille." />
        <div class="mt-12 grid divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 md:grid-cols-3 md:divide-x md:divide-y-0">
          <article v-for="step in workflow" :key="step.title" class="bg-white p-7">
            <component :is="step.icon" class="h-8 w-8 text-sky-blue-700" aria-hidden="true" />
            <h2 class="mt-5 text-lg font-bold text-slate-950">{{ step.title }}</h2>
            <p class="mt-3 text-sm leading-6 text-slate-600">{{ step.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="bg-slate-50 py-20 sm:py-28">
      <div class="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div id="purchase-mode-panel" role="tabpanel" :aria-labelledby="`purchase-mode-${selectedPurchaseMode}`">
          <Transition enter-active-class="transition duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]" enter-from-class="translate-y-1 opacity-0" enter-to-class="translate-y-0 opacity-100" leave-active-class="transition duration-150 ease-out" leave-from-class="translate-y-0 opacity-100" leave-to-class="-translate-y-1 opacity-0" mode="out-in">
          <MarketingProductPreview
            :key="selectedPurchaseMode"
            type="decision"
            :src="selectedPurchasePreview.src"
            :alt="selectedPurchasePreview.alt"
            :caption="selectedPurchasePreview.caption"
          />
          </Transition>
        </div>
        <div>
          <MarketingSectionHeading align="left" eyebrow="Simple ou Avancé" title="Gardez le cadre Trident ou construisez le vôtre" description="Le mode Simple reprend la sélection et les hypothèses officielles de la Méthode Trident. Le mode Avancé ouvre les paramètres utiles sans vous obliger à reconstruire tout le processus." />
          <div class="mt-8 grid gap-4 sm:grid-cols-2" role="tablist" aria-label="Choisir le mode de la page Achat">
            <button
              v-for="(mode, modeKey) in purchaseModes"
              :id="`purchase-mode-${modeKey}`"
              :key="modeKey"
              type="button"
              role="tab"
              aria-controls="purchase-mode-panel"
              :aria-selected="selectedPurchaseMode === modeKey"
              :tabindex="selectedPurchaseMode === modeKey ? 0 : -1"
              class="min-h-11 cursor-pointer rounded-2xl border p-5 text-left transition-[background-color,border-color,box-shadow,transform] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue-500 focus-visible:ring-offset-2"
              :class="selectedPurchaseMode === modeKey
                ? 'border-sky-blue-200 bg-sky-blue-50 shadow-sm'
                : 'border-slate-200 bg-white hover:border-sky-blue-200 hover:bg-sky-blue-50/40'"
              @click="selectPurchaseMode(modeKey)"
              @keydown="handleModeKeydown($event, modeKey)"
            >
              <p class="text-sm font-bold" :class="selectedPurchaseMode === modeKey ? 'text-sky-blue-900' : 'text-slate-900'">{{ mode.label }}</p>
              <p class="mt-2 text-sm leading-6" :class="selectedPurchaseMode === modeKey ? 'text-sky-blue-900/75' : 'text-slate-600'">{{ mode.description }}</p>
            </button>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-slate-950 py-20 sm:py-28">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeading inverse eyebrow="Après l’achat" title="Vérifiez si votre stratégie produit le résultat attendu" description="Enregistrez vos transactions, comparez chaque position depuis sa date réelle d’achat et suivez l’évolution du portefeuille dans son ensemble." />
        <div class="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <MarketingProductPreview type="analyze" :src="MARKETING_SCREENSHOTS.performance" alt="Suivi de performance AppTrident comparant une transaction avec un indice" caption="Performance totale ou annualisée, comparaison avec un indice, une autre action ou le fondamental de l’entreprise." />
            <h2 class="mt-6 text-xl font-bold text-white">Mesurez vos décisions réelles</h2>
            <p class="mt-3 text-sm leading-6 text-slate-300">Les performances partent de vos dates et prix d’achat, et non d’une période théorique qui ne correspond pas à votre portefeuille.</p>
          </div>
          <div>
            <MarketingProductPreview type="progress" :src="MARKETING_SCREENSHOTS.progression" alt="Tableau de bord AppTrident suivant la progression vers un objectif de revenus passifs" caption="Suivez le capital, les dépôts, les gains et une estimation prudente du revenu mensuel soutenable." />
            <h2 class="mt-6 text-xl font-bold text-white">Gardez une vision long terme</h2>
            <p class="mt-3 text-sm leading-6 text-slate-300">Les niveaux de progression rendent visible le chemin parcouru, même lorsque les mouvements de marché à court terme brouillent la tendance.</p>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-white py-20 sm:py-28">
      <div class="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
        <MarketingSectionHeading align="left" eyebrow="Un portefeuille qui vous ressemble" title="Plusieurs comptes, devises et objectifs, dans une vue cohérente" description="Gérez plusieurs portefeuilles, adaptez les montants et fréquences de versement, configurez votre fiscalité et réunissez vos comptes dans le tableau de progression." />
        <div class="grid gap-4 sm:grid-cols-2">
          <div v-for="item in [{ icon: ScaleIcon, title: 'Allocation', text: 'Visualisez le poids de chaque position et les écarts de répartition.' }, { icon: AdjustmentsHorizontalIcon, title: 'Paramètres', text: 'Adaptez devises, contributions, fiscalité et hypothèses de portefeuille.' }, { icon: ArrowTrendingUpIcon, title: 'Performance', text: 'Mesurez chaque transaction et le portefeuille complet sur la bonne période.' }, { icon: CheckCircleIcon, title: 'Progression', text: 'Suivez les dépôts, gains et paliers vers votre objectif de revenus passifs.' }]" :key="item.title" class="flex gap-4 border-t border-slate-200 py-5"><component :is="item.icon" class="mt-0.5 h-6 w-6 shrink-0 text-sky-blue-700" /><div><h3 class="font-bold text-slate-950">{{ item.title }}</h3><p class="mt-2 text-sm leading-6 text-slate-600">{{ item.text }}</p></div></div>
        </div>
      </div>
    </section>

    <MarketingFinalCta page="decider-suivre" title="Passez de l’analyse à une routine d’investissement suivie dans le temps." description="Accédez à la page Achat, au suivi de performance, à la progression et à tout l’écosystème AppTrident." />
  </div>
</template>
